import { createTheme, ThemeOptions } from "@mui/material/styles";

// Extend the TypeBackground interface to include our custom gradient property
declare module '@mui/material/styles' {
  interface TypeBackground {
    gradient?: string;
  }
}

/**
 * Tema principal do aplicativo - Verde Menta sobre fundo escuro
 * 
 * Este arquivo define o tema centralizado para todo o aplicativo, usando um esquema de
 * cores escuro com destaque em verde menta (#00BFA5) como cor principal.
 * As cores do tema estão também disponíveis como constantes em themeConstants.ts para
 * uso em componentes que precisam de referências diretas às cores.
 */
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#00BFA5", // Verde menta principal
      light: "#4ECDB5",
      dark: "#00897B",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF8A65", // Laranja para accent
      light: "#FFA78C",
      dark: "#C75B39",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#f44336",
      light: "#e57373",
      dark: "#d32f2f",
    },
    warning: {
      main: "#ff9800",
      light: "#ffb74d",
      dark: "#f57c00",
    },
    info: {
      main: "#29b6f6",
      light: "#4fc3f7",
      dark: "#0288d1",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#66bb6a",
      light: "#81c784",
      dark: "#388e3c",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0B0B0", 
      disabled: "rgba(255, 255, 255, 0.5)",
    },
    background: {
      default: "#1E1E1E", // Escuro como na interface do Claude
      paper: "#2D2D2D", // Um pouco mais claro para cards e elementos
      gradient: "linear-gradient(180deg, #222222 0%, #1A1A1A 100%)",
    },
    divider: "rgba(255, 255, 255, 0.12)",
  },
  typography: {
    fontFamily: [
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h1: {
      fontSize: "2.5rem",
      fontWeight: 600,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 600,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#1E1E1E", // Background escuro
          color: "#FFFFFF",
          minHeight: "100vh",
          // Global scrollbar styling
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: 'rgba(176, 176, 176, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(176, 176, 176, 0.5)',
            },
          },
          '*::-webkit-scrollbar-corner': {
            background: 'transparent',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          },
        },
        contained: {
          backgroundColor: "#00BFA5", // Verde menta
          color: "white",
          "&:hover": {
            backgroundColor: "#00897B",
          },
        },
        outlined: {
          borderColor: "#00BFA5",
          color: "#00BFA5",
          "&:hover": {
            borderColor: "#4ECDB5",
            backgroundColor: "rgba(0, 191, 165, 0.08)",
          },
        },
        text: {
          color: "#00BFA5",
          "&:hover": {
            backgroundColor: "rgba(0, 191, 165, 0.08)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#2D2D2D", // Um pouco mais claro que o background
          borderRadius: 12,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          border: "1px solid #3A3A3A",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#2D2D2D",
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-input": {
            color: "#FFFFFF",
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#3A3A3A",
            borderRadius: 8,
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00BFA5",
                borderWidth: 2,
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#555555",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#777777",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#B0B0B0",
            "&.Mui-focused": {
              color: "#00BFA5",
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#555555",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#777777",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00BFA5",
            borderWidth: 2,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#2D2D2D",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#252525",
          borderRight: "1px solid #3A3A3A",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#00BFA5",
          fontWeight: 500,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&.Mui-selected": {
            backgroundColor: "rgba(0, 191, 165, 0.15)",
            "&:hover": {
              backgroundColor: "rgba(0, 191, 165, 0.25)",
            },
          },
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: "#00BFA5",
          color: "#FFFFFF",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#3A3A3A",
          color: "#FFFFFF",
          fontSize: "0.8rem",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: "rgba(102, 187, 106, 0.15)",
          color: "#81c784",
        },
        standardInfo: {
          backgroundColor: "rgba(41, 182, 246, 0.15)",
          color: "#4fc3f7",
        },
        standardWarning: {
          backgroundColor: "rgba(255, 152, 0, 0.15)",
          color: "#ffb74d",
        },
        standardError: {
          backgroundColor: "rgba(244, 67, 54, 0.15)",
          color: "#e57373",
        },
      },
    },
  },
});

export default theme;